// FILE: /src/Components/Assets/UploadProgress.js (NEW FILE)

import React, { useEffect, useState } from 'react';
import { Button, Progress, Spin } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, CloseOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { formatBytes } from '../../utils/formatters';

// Sub-component for individual file progress
const FileItem = ({ item, onCancelUpload }) => {
    const [previewUrl, setPreviewUrl] = useState(null);
    const isImage = item.file.type.startsWith('image/');

    useEffect(() => {
        // Create a temporary local URL for image previews to avoid unnecessary network requests
        if (isImage) {
            const url = URL.createObjectURL(item.file);
            setPreviewUrl(url);
            // Clean up the object URL when the component unmounts
            return () => URL.revokeObjectURL(url);
        }
    }, [item.file, isImage]);

    return (
        <div className="relative flex items-center gap-3 p-2 rounded-md overflow-hidden bg-white border border-gray-200">
            {/* Progress bar as the background */}
            <div
                className="absolute top-0 left-0 h-full bg-blue-50 transition-all duration-300"
                style={{ width: `${item.progress}%` }}
            />
            
            {/* Content sits on top of the progress bar */}
            <div className="relative flex-shrink-0 w-12 h-12 rounded-md bg-gray-100 flex items-center justify-center overflow-hidden">
                {isImage && previewUrl ? (
                    <img src={previewUrl} alt={item.file.name} className="w-full h-full object-cover" />
                ) : (
                    <VideoCameraOutlined className="text-gray-500 text-2xl" />
                )}
            </div>

            <div className="relative flex-grow min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">{item.file.name}</p>
                <div className="flex items-center text-xs text-gray-500">
                    <span>{formatBytes(item.file.size)}</span>
                    <span className="mx-1">•</span>
                    {item.status === 'uploading' && <span className="text-blue-600 font-semibold">Uploading...</span>}
                    {item.status === 'completed' && <span className="text-green-600 font-semibold">Complete</span>}
                    {item.status === 'failed' && <span className="text-red-600 font-semibold truncate">Failed: {item.error}</span>}
                </div>
            </div>
            
            {item.status === 'uploading' && (
                <Button
                    type="text"
                    shape="circle"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => onCancelUpload(item.id)}
                    className="relative"
                />
            )}
        </div>
    );
};

const UploadProgress = ({ uploadQueue, onClearCompleted, onCancelUpload, onClose }) => {
    if (uploadQueue.length === 0) {
        return null;
    }

    const completedCount = uploadQueue.filter(f => f.status === 'completed').length;
    const isUploading = uploadQueue.some(f => f.status === 'uploading');
    const failedCount = uploadQueue.filter(f => f.status === 'failed').length;

    // Calculate overall progress
    const totalSize = uploadQueue.reduce((acc, item) => acc + item.file.size, 0);
    const uploadedSize = uploadQueue.reduce((acc, item) => acc + (item.file.size * (item.progress / 100)), 0);
    const overallProgress = totalSize > 0 ? (uploadedSize / totalSize) * 100 : 0;

    let title = "Uploads Complete";
    if (isUploading) {
        const uploadingCount = uploadQueue.filter(f => f.status === 'uploading').length;
        title = `Uploading ${uploadingCount} item(s)...`;
    } else if (failedCount > 0) {
        title = `${completedCount} succeeded, ${failedCount} failed`;
    }

    return (
        <div className="fixed bottom-4 right-4 w-96 bg-gray-50 rounded-lg shadow-2xl border z-50 animate-fade-in-up">
            <header className="flex justify-between items-center p-3 border-b rounded-t-lg">
                <div className="flex items-center gap-2">
                    {isUploading && <Spin size="small" />}
                    {!isUploading && completedCount > 0 && <CheckCircleFilled className="text-green-500" />}
                    {!isUploading && failedCount > 0 && completedCount === 0 && <CloseCircleFilled className="text-red-500" />}
                    <h3 className="font-semibold text-sm text-gray-800">{title}</h3>
                </div>
                <div>
                    {!isUploading && completedCount > 0 && (
                        <Button size="small" type="link" onClick={onClearCompleted}>Clear Completed</Button>
                    )}
                    <Button size="small" type="text" icon={<CloseOutlined />} onClick={onClose} />
                </div>
            </header>
            
            {isUploading && <Progress percent={overallProgress} showInfo={false} size="small" strokeColor="#3b82f6" />}

            <div className="p-3 max-h-64 overflow-y-auto space-y-2">
                {uploadQueue.map(item => (
                    <FileItem key={item.id} item={item} onCancelUpload={onCancelUpload} />
                ))}
            </div>
        </div>
    );
};

export default UploadProgress;